import React, { useState, useEffect, useRef, useMemo } from 'react';
import api from '../services/api';
import ModalNovaContaPagar from '../components/financeiro/ModalNovaContaPagar';
import s from './PedidosOrcamentos.module.css';
import { useRolarLinha } from '../hooks/useNavegacaoLista';

/* ── Helpers ── */
const FISCAL_ICMS_DEFAULT = {
  icms_habilitado: true,
  icms_mod_bc: '3',
  icms_red_bc_perc: '0.00', icms_aliq_perc: '0.00',
  icms_aliq_efet_perc: '0.00', icms_dif_perc: '0.00',
  icms_st_mod_bc: '4',
  icms_st_red_bc_perc: '0.00', icms_st_mva_perc: '0.00', icms_st_aliq_perc: '0.00',
  icms_st_ret_bc: '0.00', icms_st_ret_valor: '0.00',
};

const fmtMoeda = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum   = v => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const fmtData  = str => str ? new Date(String(str).slice(0, 10) + 'T12:00:00').toLocaleDateString('pt-BR') : '—';

const TIPO_FRETE_LABEL = {
  '0': 'Emitente (CIF)', '1': 'Destinatário (FOB)', '2': 'Terceiros',
  '3': 'Próprio/Remetente', '4': 'Próprio/Destinatário', '9': 'Sem frete',
};
const TPAG_LABEL = {
  '01': 'Dinheiro', '02': 'Cheque', '03': 'Cartão de Crédito', '04': 'Cartão de Débito',
  '05': 'Crédito Loja', '10': 'Vale Alimentação', '11': 'Vale Refeição', '12': 'Vale Presente',
  '13': 'Vale Combustível', '15': 'Boleto', '17': 'PIX', '90': 'Sem pagamento', '99': 'Outros',
};

const STATUS_ICON = {
  encontrado:      { ico: '🟢', txt: 'Localizado',     cor: '#2e7d32' },
  parcial:         { ico: '🟡', txt: 'Parcial',        cor: '#a16207' },
  nao_encontrado:  { ico: '🔴', txt: 'Não localizado', cor: '#b71c1c' },
  novo:            { ico: '🔵', txt: 'Cadastrado',     cor: '#1a56db' },
  inativo:         { ico: '🟠', txt: 'Inativo',        cor: '#c2410c' },
};

// Converte CFOP de entrada para saída e descreve CSOSN/CST
const CFOP_CONV = {
  '1102': { saida: '5102', desc: 'Compra p/ comercialização' },
  '1101': { saida: '5101', desc: 'Compra p/ industrialização' },
  '1202': { saida: '5202', desc: 'Devolução de venda' },
  '1403': { saida: '5403', desc: 'Compra p/ comercialização ST' },
  '1401': { saida: '5401', desc: 'Compra p/ industrialização ST' },
  '1411': { saida: '5411', desc: 'Devolução compra ST' },
  '1556': { saida: '5556', desc: 'Compra de bem p/ ativo' },
  '1910': { saida: '5910', desc: 'Remessa p/ demonstração' },
  '2102': { saida: '6102', desc: 'Compra p/ comerc. (interestad.)' },
  '2101': { saida: '6101', desc: 'Compra p/ indust. (interestad.)' },
  '2403': { saida: '6403', desc: 'Compra p/ comerc. ST (interestad.)' },
  '2556': { saida: '6556', desc: 'Compra ativo (interestad.)' },
  '3102': { saida: '6102', desc: 'Importação p/ comercialização' },
};
const CSOSN_DESC = {
  '101': 'Créd. ICMS SN',
  '102': 'Sem crédito SN',
  '201': 'Créd. ICMS c/ ST SN',
  '202': 'Sem créd. c/ ST SN',
  '203': 'Sem créd. c/ ST SN',
  '300': 'Imune SN',
  '400': 'Não tributado SN',
  '500': 'ICMS ST anterior',
  '900': 'Outros SN',
  '00':  'Tributada integ.',
  '10':  'Tributada c/ ST',
  '20':  'Com redução BC',
  '30':  'Isenta c/ ST',
  '40':  'Isenta',
  '41':  'Não tributada',
  '50':  'Suspensão',
  '51':  'Diferimento',
  '60':  'ICMS cobrado ant.',
  '70':  'Red. BC c/ ST',
  '90':  'Outros',
};
const cfopLabel = (cfop) => {
  const conv = CFOP_CONV[cfop];
  return conv ? `${cfop}→${conv.saida}` : cfop;
};
const csosnLabel = (csosn) => CSOSN_DESC[csosn] ? `${csosn} ${CSOSN_DESC[csosn]}` : csosn;

/* ══════════════════════════════════
   Modal: Busca de Produto (inline)
══════════════════════════════════ */
function ModalBuscaProduto({ onSelect, onClose }) {
  const [buscaRapida, setBuscaRapida] = useState('');
  const [produtos, setProdutos]       = useState([]);
  const [buscando, setBuscando]       = useState(false);
  const [idx, setIdx]                 = useState(null);
  const refLinhaProd = useRolarLinha(idx);
  const inputRef = useRef(null);
  const debRef   = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 60); }, []);

  const buscar = async (texto) => {
    setBuscando(true); setIdx(null);
    try {
      const { data } = await api.get('/produtos', { params: texto ? { busca: texto, limite: 200 } : { limite: 50 } });
      setProdutos(Array.isArray(data) ? data : []);
    } catch { setProdutos([]); }
    finally { setBuscando(false); }
  };

  useEffect(() => { buscar(''); }, []); // eslint-disable-line

  const handleRapida = e => {
    const v = e.target.value;
    setBuscaRapida(v);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => buscar(v), 300);
  };

  useEffect(() => {
    const h = e => {
      if (document.querySelector('[data-senha-admin]')) return; // diálogo de senha aberto
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
      if (e.key === 'Enter' && idx !== null) { e.preventDefault(); onSelect(produtos[idx]); }
      if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => i === null ? 0 : Math.min(i + 1, produtos.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setIdx(i => i === null ? 0 : Math.max(i - 1, 0)); }
    };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, [idx, produtos]); // eslint-disable-line

  return (
    <div className={s.overlay} style={{ zIndex: 2200 }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={s.modalF3}>
        <div className={s.modalTitle} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Selecionar Produto</span>
          <button style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16 }}
            onClick={onClose}>✕</button>
        </div>
        <div className={s.f3GridWrap}>
          <table className={s.f3Grid}>
            <thead><tr>
              <th style={{ width: 85 }}>Código</th>
              <th>Descrição</th>
              <th style={{ width: 42 }}>UN</th>
              <th style={{ width: 90, textAlign: 'right' }}>Estoque</th>
            </tr></thead>
            <tbody>
              {produtos.length === 0 && !buscando && (
                <tr><td colSpan={4} className={s.f3Vazio}>Use a busca rápida abaixo.</td></tr>
              )}
              {buscando && (<tr><td colSpan={4} className={s.f3Vazio}>Buscando...</td></tr>)}
              {produtos.map((p, i) => (
                <tr key={p.id}
                  ref={idx === i ? refLinhaProd : null}
                  className={idx === i ? s.f3LinhaSel : ''}
                  onClick={() => setIdx(i)}
                  onDoubleClick={() => onSelect(p)}>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{p.codigo_interno}</td>
                  <td>{p.nome}</td>
                  <td>{p.unidade || ''}</td>
                  <td style={{ textAlign: 'right' }}>{Number(p.estoque_total || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={s.f3Rodape}>
          <div className={s.f3BuscaRapidaWrap}>
            <label className={s.f3Label}>Busca:</label>
            <input ref={inputRef} className={s.f3BuscaRapida}
              placeholder="Nome ou código..."
              value={buscaRapida} onChange={handleRapida}
              autoComplete="off" />
          </div>
          <div className={s.f3BotoesRodape}>
            <button className={s.f3BtnOk} disabled={idx === null}
              onClick={() => idx !== null && onSelect(produtos[idx])}>Ok</button>
            <button className={s.f3BtnSair} onClick={onClose}>Sair</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   Modal: Cadastro Rápido de Fornecedor
══════════════════════════════════ */
function ModalCadastroFornecedorRapido({ dadosXml, onSalvo, onCancelar }) {
  const ehCpf = !dadosXml.cnpj && dadosXml.cpf;
  const [f, setF] = useState({
    nome_fantasia: dadosXml.nome_fantasia || '',
    razao_social:  dadosXml.razao_social || '',
    cpf_cnpj:      dadosXml.cnpj || dadosXml.cpf || '',
    ie:            dadosXml.ie || '',
    cep:           dadosXml.cep || '',
    logradouro:    dadosXml.logradouro || '',
    numero:        dadosXml.numero || '',
    complemento:   dadosXml.complemento || '',
    bairro:        dadosXml.bairro || '',
    cidade:        dadosXml.cidade || '',
    uf:            dadosXml.uf || '',
    fone:          dadosXml.fone || '',
    email:         '',
    tipo_pessoa:   ehCpf ? 'PF' : 'PJ',
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro]         = useState('');

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const salvar = async () => {
    if (!f.nome_fantasia.trim()) { setErro('Nome Fantasia é obrigatório.'); return; }
    setSalvando(true); setErro('');
    try {
      const { data } = await api.post('/fornecedores', {
        nome_fantasia:     f.nome_fantasia.trim(),
        razao_social:      f.razao_social.trim(),
        cpf_cnpj:          f.cpf_cnpj.trim(),
        tipo_contribuinte: f.tipo_pessoa === 'PF' ? 'fisica' : 'juridica',
        ie:                f.ie.trim(),
        cep:               f.cep.trim(),
        logradouro:        f.logradouro.trim(),
        numero:            f.numero.trim(),
        complemento:       f.complemento.trim(),
        bairro:            f.bairro.trim(),
        cidade:            f.cidade.trim(),
        uf:                f.uf.trim(),
        fone:              f.fone.trim(),
        email:             f.email.trim(),
      });
      onSalvo(data);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar fornecedor.');
      setSalvando(false);
    }
  };

  const campo = (label, k, w = '100%', tipo = 'text') => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, width: w }}>
      <label style={{ fontSize: 11, color: '#555', fontWeight: 'bold' }}>{label}</label>
      <input className={s.inputErp} type={tipo} value={f[k]} onChange={e => set(k, e.target.value)} />
    </div>
  );

  return (
    <div className={s.overlay} style={{ zIndex: 2200 }} onMouseDown={e => e.target === e.currentTarget && onCancelar()}>
      <div className={s.modal} style={{ width: 720, maxWidth: '95%' }}>
        <div className={s.modalTitle} style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Cadastro Rápido de Fornecedor</span>
          <button style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 15 }}
            onClick={onCancelar}>✕</button>
        </div>
        <div className={s.modalBody}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {campo('Nome Fantasia', 'nome_fantasia', 'calc(50% - 4px)')}
            {campo('Razão Social', 'razao_social', 'calc(50% - 4px)')}
            {campo('CNPJ / CPF', 'cpf_cnpj', 'calc(33% - 6px)')}
            {campo('Inscrição Estadual', 'ie', 'calc(33% - 6px)')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, width: 'calc(34% - 6px)' }}>
              <label style={{ fontSize: 11, color: '#555', fontWeight: 'bold' }}>Tipo de Pessoa</label>
              <select className={s.selectErp} value={f.tipo_pessoa} onChange={e => set('tipo_pessoa', e.target.value)}>
                <option value="PJ">Pessoa Jurídica</option>
                <option value="PF">Pessoa Física</option>
              </select>
            </div>
            {campo('CEP', 'cep', 'calc(25% - 6px)')}
            {campo('Logradouro', 'logradouro', 'calc(50% - 6px)')}
            {campo('Número', 'numero', 'calc(25% - 6px)')}
            {campo('Complemento', 'complemento', 'calc(33% - 6px)')}
            {campo('Bairro', 'bairro', 'calc(33% - 6px)')}
            {campo('Cidade', 'cidade', 'calc(26% - 6px)')}
            {campo('UF', 'uf', 'calc(8% - 6px)')}
            {campo('Telefone', 'fone', 'calc(50% - 4px)')}
            {campo('Email', 'email', 'calc(50% - 4px)', 'email')}
          </div>
          {erro && <div className={s.statusErro} style={{ marginTop: 6 }}>{erro}</div>}
        </div>
        <div className={s.modalFooter}>
          <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={salvar} disabled={salvando}>
            {salvando ? 'Salvando...' : 'Salvar Fornecedor'}
          </button>
          <button className={s.btnErp} onClick={onCancelar} disabled={salvando}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   Cabeçalho de seção colapsável
══════════════════════════════════ */
function SecaoHeader({ titulo, aberto, onToggle, extra }) {
  return (
    <div onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
        background: '#d0dcea', border: '1px solid #9aafca', padding: '4px 10px',
        fontWeight: 'bold', fontSize: 13, color: '#003087', userSelect: 'none',
      }}>
      <span style={{ width: 14 }}>{aberto ? '▼' : '▶'}</span>
      <span>{titulo}</span>
      {extra && <span style={{ marginLeft: 'auto', fontWeight: 'normal', color: '#444' }}>{extra}</span>}
    </div>
  );
}

/* ══════════════════════════════════
   Wizard principal
══════════════════════════════════ */
export default function ImportacaoXMLWizard({ xmlData, xmlContent, onConfirmar, onCancelar }) {
  const { validacao = {}, fornecedor = {}, dados = {}, financeiro = {}, transporte = {} } = xmlData || {};

  // ── Itens (estado mutável) ──
  // Produto já localizado: começa com a descrição/qtde por embalagem que já
  // está no cadastro (não o texto cru do XML) — assim, se o operador não
  // mexer em nada, o confirmar não sobrescreve o cadastro com nada diferente;
  // só grava de volta no produto quando ele realmente ajustar o campo.
  const [itens, setItens] = useState(() =>
    (xmlData.itens || []).map((it, i) => ({
      ...it,
      _key: `${it.seq}-${i}`,
      descricao_produto: it.produto_id ? (it.produto_nome || '') : (it.descricao || ''),
      qtde_produto: it.produto_id ? (it.produto_fator_conversao || 1) : 1,
    }))
  );

  // ── Fornecedor ── cadastro é OBRIGATÓRIO pra prosseguir (sem isso, contas a
  // pagar geradas pela nota ficariam sem fornecedor vinculado).
  const [fornecedorId, setFornecedorId]       = useState(fornecedor.cadastro?.id || null);
  const [fornecedorCad, setFornecedorCad]     = useState(fornecedor.cadastro || null);
  const [showCadFornec, setShowCadFornec]     = useState(false);

  // ── Contas a Pagar avulso (fallback manual quando o XML não trouxe duplicata) ──
  const [modalCPAvulso, setModalCPAvulso]     = useState(false);
  const [contasFinancCP, setContasFinancCP]   = useState([]);
  const [planoContasCP, setPlanoContasCP]     = useState([]);
  const abrirCPAvulso = () => {
    api.get('/financeiro/contas').then(r => setContasFinancCP(r.data || [])).catch(() => {});
    api.get('/financeiro/plano-contas/analiticas', { params: { tipo: 'despesa,custo' } })
      .then(r => setPlanoContasCP(r.data || [])).catch(() => {});
    setModalCPAvulso(true);
  };

  // ── Modais e expansões ──
  const [buscaProdKey, setBuscaProdKey] = useState(null); // _key do item
  const [expandKey, setExpandKey]       = useState(null); // _key do item em cadastro

  // ── Colapsáveis ──
  const [openFin,   setOpenFin]   = useState(false);
  const [openTransp, setOpenTransp] = useState(false);

  // ── Seleção em lote ──
  const [selecionados, setSelecionados] = useState(new Set());
  const [cadBatch, setCadBatch] = useState({ unidade_id: '', grupo_id: '', subgrupo_id: '' });
  const [grupos,    setGrupos]    = useState([]);
  const [subgrupos, setSubgrupos] = useState([]);
  const [unidades,  setUnidades]  = useState([]);
  const [cadastrando, setCadastrando] = useState(false);

  // ── Mini-forms de cadastro rápido ──
  const [novaUnidade,  setNovaUnidade]  = useState({ show: false, codigo: '', descricao: '', salvando: false });
  const [novoGrupo,    setNovoGrupo]    = useState({ show: false, nome: '', salvando: false });
  const [novoSubgrupo, setNovoSubgrupo] = useState({ show: false, nome: '', salvando: false });

  // ── Contas a pagar ──
  const [gerandoContas,    setGerandoContas]    = useState(false);
  const [contasJaGeradas,  setContasJaGeradas]  = useState(false);

  // ── Tipo de Movimento de Entrada + Conversões ──
  const [tiposEntrada,      setTiposEntrada]      = useState([]);
  const [tipoMovimentoId,   setTipoMovimentoId]   = useState('');
  const [cfopConvMap,       setCfopConvMap]        = useState({});
  const [cstIcmsConvMap,    setCstIcmsConvMap]    = useState({});
  const [cstPisConvMap,     setCstPisConvMap]     = useState({});

  const fornecedorOk = !!fornecedorId;
  const chaveDuplicada = !!validacao.chave_duplicada;
  const errosValidacao = validacao.erros || [];

  const temSelecionados = selecionados.size > 0;

  /* ── Carregar auxiliares ── */
  useEffect(() => {
    api.get('/grupos').then(r => setGrupos(r.data || [])).catch(() => {});
    api.get('/unidades').then(r => setUnidades(r.data || [])).catch(() => {});
    api.get('/fiscal/tipos-movimento', { params: { tipo: 'E' } })
      .then(r => setTiposEntrada(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!cadBatch.grupo_id) { setSubgrupos([]); return; }
    api.get(`/subgrupos?grupo_id=${cadBatch.grupo_id}`).then(r => setSubgrupos(r.data || [])).catch(() => {});
  }, [cadBatch.grupo_id]);

  useEffect(() => {
    if (!tipoMovimentoId) {
      setCfopConvMap({}); setCstIcmsConvMap({}); setCstPisConvMap({});
      return;
    }
    Promise.all([
      api.get(`/fiscal/tipos-movimento/${tipoMovimentoId}/conversoes-cfop`),
      api.get(`/fiscal/tipos-movimento/${tipoMovimentoId}/conversoes-cst-icms`),
      api.get(`/fiscal/tipos-movimento/${tipoMovimentoId}/conversoes-cst-pis-cofins`),
    ]).then(([cfopR, icmsR, pisR]) => {
      const cfop = {};
      (cfopR.data || []).forEach(c => { cfop[c.cfop_xml] = c.cfop_entrada; });
      const icms = {};
      (icmsR.data || []).forEach(c => { icms[c.cst_xml] = { entrada: c.cst_entrada, saida: c.cst_saida }; });
      const pis = {};
      (pisR.data || []).forEach(c => { pis[c.cst_xml] = { entrada: c.cst_entrada, saida: c.cst_saida }; });
      setCfopConvMap(cfop);
      setCstIcmsConvMap(icms);
      setCstPisConvMap(pis);
    }).catch(() => { setCfopConvMap({}); setCstIcmsConvMap({}); setCstPisConvMap({}); });
  }, [tipoMovimentoId]);

  /* ── Resumo de matching ── */
  const resumo = useMemo(() => {
    const localizados = itens.filter(i => i.produto_id).length;
    const naoLocal    = itens.length - localizados;
    return { total: itens.length, localizados, naoLocal };
  }, [itens]);

  const podeConfirmar = !chaveDuplicada && errosValidacao.length === 0 && fornecedorOk;

  /* ── Atualizar um item ── */
  const patchItem = (key, patch) =>
    setItens(arr => arr.map(it => it._key === key ? { ...it, ...patch } : it));

  /* ── Checkbox helpers ── */
  const toggleSel = key => setSelecionados(s => {
    const n = new Set(s);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  const naoLocalizados = itens.filter(i => !i.produto_id);
  const allNaoLocChecked = naoLocalizados.length > 0 &&
    naoLocalizados.every(i => selecionados.has(i._key));

  const toggleSelectAll = () => {
    if (allNaoLocChecked) {
      setSelecionados(s => {
        const n = new Set(s);
        naoLocalizados.forEach(i => n.delete(i._key));
        return n;
      });
    } else {
      setSelecionados(s => {
        const n = new Set(s);
        naoLocalizados.forEach(i => n.add(i._key));
        return n;
      });
    }
  };

  /* ── Salvar associação de aprendizado ── */
  const salvarAssociacao = async (it, produtoId) => {
    if (!it.codigo_fornecedor) return;
    try {
      await api.post('/entradas-nf/salvar-associacao', {
        produto_id:          produtoId,
        fornecedor_id:       fornecedorId,
        codigo_fornecedor:   it.codigo_fornecedor,
        descricao_fornecedor: it.descricao,
      });
    } catch { /* silencioso */ }
  };

  // A mesma nota pode repetir o produto em várias linhas (mesmo EAN/cProd).
  // Vincular uma linha vincula as irmãs ainda não localizadas — senão o
  // "Cadastrar" da segunda linha bate no EAN duplicado e ela fica órfã.
  const mesmoProdutoXml = (a, b) =>
    (a.ean && a.ean === b.ean) || (a.codigo_fornecedor && a.codigo_fornecedor === b.codigo_fornecedor);

  const vincularIrmaos = (origem, patch) =>
    setItens(arr => arr.map(x =>
      x._key !== origem._key && !x.produto_id && mesmoProdutoXml(x, origem) ? { ...x, ...patch } : x));

  /* ── Selecionar produto da busca ── */
  const onSelecionarProduto = async (key, p) => {
    const it = itens.find(x => x._key === key);
    if (it) vincularIrmaos(it, {
      produto_id: p.id, produto_nome: p.nome, produto_codigo: p.codigo_interno,
      status: 'encontrado', match_tipo: 'manual',
      descricao_produto: p.nome || '', qtde_produto: p.fator_conversao || 1,
    });
    patchItem(key, {
      produto_id:     p.id,
      produto_nome:   p.nome,
      produto_codigo: p.codigo_interno,
      status:         'encontrado',
      match_tipo:     'manual',
      // mesma lógica do match automático: começa com o que já está no
      // cadastro, só muda de verdade se o operador editar depois
      descricao_produto: p.nome || '',
      qtde_produto:       p.fator_conversao || 1,
    });
    setBuscaProdKey(null);
    if (it) await salvarAssociacao(it, p.id);
  };

  /* ── Cadastrar em lote ── */
  const handleCadastrarLote = async () => {
    const itensSel = itens.filter(it => selecionados.has(it._key) && !it.produto_id);
    if (itensSel.length === 0) return;
    setCadastrando(true);
    let erros = [];
    const jaVinculados = itens.filter(x => x.produto_id)
      .map(x => ({ ...x, produto: { id: x.produto_id, nome: x.produto_nome, codigo_interno: x.produto_codigo } }));
    for (const it of itensSel) {
      const irmao = jaVinculados.find(x => mesmoProdutoXml(x, it));
      if (irmao) {
        patchItem(it._key, {
          produto_id:     irmao.produto.id,
          produto_nome:   irmao.produto.nome,
          produto_codigo: irmao.produto.codigo_interno,
          status:         'encontrado',
          match_tipo:     'manual',
        });
        setSelecionados(s => { const n = new Set(s); n.delete(it._key); return n; });
        continue;
      }
      try {
        const custo = parseFloat(it.valor_unitario || 0);
        const margem = 30;
        const unidSel = unidades.find(u => String(u.id) === String(cadBatch.unidade_id));
        const unidCodigo = unidSel?.codigo || it.unidade || 'UN';
        const cstXml   = it.csosn || it.cst_icms || '';
        const pisXml   = it.cst_pis || '';
        const icmsConv = cstIcmsConvMap[cstXml];
        const pisConv  = cstPisConvMap[pisXml];
        const cfopConv = cfopConvMap[it.cfop] || it.cfop;
        const payload = {
          nome:               (it.descricao_produto || it.descricao || '').trim(),
          descricao_resumida: (it.descricao_produto || it.descricao || '').slice(0, 40),
          unidade:            unidCodigo,
          ncm:                it.ncm || null,
          cfop_saida:         CFOP_CONV[cfopConv]?.saida || CFOP_CONV[it.cfop]?.saida || it.cfop || null,
          ean_fiscal:         it.ean || null,
          grupo_id:           cadBatch.grupo_id || null,
          subgrupo_id:        cadBatch.subgrupo_id || null,
          preco_custo:        custo,
          margem_pct:         margem,
          preco_venda:        parseFloat((custo * (1 + margem / 100)).toFixed(2)),
          fator_conversao:    parseFloat(it.qtde_produto || 1),
          ativo:              true,
          inativo:            false,
          // ean_cprod: fornecedor mandou "SEM GTIN" no cEAN mas o GTIN no cProd
          // — vai pros códigos de barras (bipe no PDV e o próximo XML acha o
          // produto), mas não pro ean_fiscal, que segue o que a nota declarou.
          codigos_barras:     (it.ean || it.ean_cprod)
            ? [{ codigo: it.ean || it.ean_cprod, tipo: 'EAN-13', principal: true, quantidade: parseFloat(it.qtde_produto || 1) }] : [],
          fiscal_entrada: {
            ...FISCAL_ICMS_DEFAULT,
            icms_cst: icmsConv?.entrada || '',
            pis_habilitado: !!pisConv?.entrada, pis_cst: pisConv?.entrada || '',
            cofins_habilitado: !!pisConv?.entrada, cofins_cst: pisConv?.entrada || '',
          },
          fiscal_saida: {
            ...FISCAL_ICMS_DEFAULT,
            icms_cst: icmsConv?.saida || '',
            pis_habilitado: !!pisConv?.saida, pis_cst: pisConv?.saida || '',
            cofins_habilitado: !!pisConv?.saida, cofins_cst: pisConv?.saida || '',
          },
        };
        const { data } = await api.post('/produtos', payload);
        patchItem(it._key, {
          produto_id:     data.id,
          produto_nome:   data.nome,
          produto_codigo: data.codigo_interno,
          status:         'novo',
          match_tipo:     'novo',
        });
        jaVinculados.push({ ...it, produto: data });
        vincularIrmaos(it, {
          produto_id: data.id, produto_nome: data.nome, produto_codigo: data.codigo_interno,
          status: 'encontrado', match_tipo: 'manual',
        });
        setSelecionados(s => { const n = new Set(s); n.delete(it._key); return n; });
        // salvar associação fornecedor→produto
        if (it.codigo_fornecedor) {
          api.post('/entradas-nf/salvar-associacao', {
            produto_id: data.id, fornecedor_id: fornecedorId,
            codigo_fornecedor: it.codigo_fornecedor, descricao_fornecedor: it.descricao,
          }).catch(() => {});
        }
      } catch (err) {
        erros.push(`${it.descricao_produto || it.descricao}: ${err.response?.data?.erro || 'erro'}`);
      }
    }
    setCadastrando(false);
    if (erros.length) alert('Erros:\n' + erros.join('\n'));
  };

  /* ── Gerar Contas a Pagar (apenas uma vez) ── */
  // Exige fornecedor cadastrado — senão a conta a pagar nasce sem vínculo com
  // ninguém (não dá pra cobrar, não aparece no relatório por fornecedor etc.).
  const handleGerarContasPagar = async () => {
    if (contasJaGeradas) return;
    if (!fornecedorOk) { alert('Cadastre o fornecedor antes de gerar as contas a pagar desta nota.'); return; }
    const dups = financeiro.duplicatas || [];
    if (dups.length === 0) return;
    setGerandoContas(true);
    try {
      for (const dup of dups) {
        await api.post('/financeiro/contas-pagar', {
          descricao:       `NF ${dados.numero_nf || ''} — Dup. ${dup.numero || ''}`,
          valor:           dup.valor,
          data_vencimento: dup.vencimento,
          fornecedor_id:   fornecedorId,
          tipo:            'pagar',
          status:          'aberto',
        });
      }
      setContasJaGeradas(true);
      alert(`${dups.length} conta(s) a pagar gerada(s) com sucesso.`);
    } catch (err) {
      alert('Erro ao gerar contas a pagar: ' + (err.response?.data?.erro || err.message));
    } finally {
      setGerandoContas(false);
    }
  };

  /* ── Confirmar importação ── */
  const [confirmando, setConfirmando] = useState(false);

  const handleConfirmar = async () => {
    setConfirmando(true);
    try {
      const itens_resolvidos = itens.map(it => {
        const cfopOriginal   = it.cfop || '';
        const cfopConvertido = cfopConvMap[cfopOriginal] || cfopOriginal;
        const cstXml         = it.csosn || it.cst_icms || '';
        const pisXml         = it.cst_pis || '';
        const icmsConv       = cstIcmsConvMap[cstXml];
        const pisConv        = cstPisConvMap[pisXml];
        const fe = {
          ...FISCAL_ICMS_DEFAULT,
          icms_cst: icmsConv?.entrada || '',
          pis_habilitado:    !!pisConv?.entrada,
          pis_cst:           pisConv?.entrada  || '',
          cofins_habilitado: !!pisConv?.entrada,
          cofins_cst:        pisConv?.entrada  || '',
        };
        const fs = {
          ...FISCAL_ICMS_DEFAULT,
          icms_cst: icmsConv?.saida || '',
          pis_habilitado:    !!pisConv?.saida,
          pis_cst:           pisConv?.saida  || '',
          cofins_habilitado: !!pisConv?.saida,
          cofins_cst:        pisConv?.saida  || '',
        };
        return {
          produto_id:        it.produto_id || null,
          produto_nome:      it.produto_nome || it.descricao_produto || it.descricao || '',
          codigo_fornecedor: it.codigo_fornecedor || '',
          descricao:         it.descricao || '',
          descricao_produto: it.descricao_produto || it.descricao || '',
          qtde_produto:      it.qtde_produto || 1,
          _ean:              it.ean || it.ean_cprod || '',
          _novo:             it.match_tipo === 'novo',
          ncm:               it.ncm || '',
          cfop:              cfopConvertido,
          cfop_xml_original: cfopOriginal,
          cfop_saida:        CFOP_CONV[cfopConvertido]?.saida || CFOP_CONV[cfopOriginal]?.saida || '',
          csosn:             it.csosn || '',
          unidade:           it.unidade || '',
          quantidade:        it.quantidade || 0,
          valor_unitario:    it.valor_unitario || 0,
          valor_total:       it.valor_total || 0,
          valor_ipi:         it.valor_ipi || 0,
          valor_icms:        it.valor_icms || 0,
          valor_icms_st:     it.valor_icms_st || 0,
          desconto:          it.desconto || 0,
          fiscal_entrada: fe,
          fiscal_saida:   fs,
          _fe: fe,
          _fs: fs,
        };
      });

      // Atualiza fiscal de produtos já localizados (existentes no sistema) —
      // e, junto, nome/descrição e quantidade por embalagem (fator_conversao)
      // SE o operador ajustou "Desc. Produto"/"Qtde Prod." no wizard; como
      // esses campos já nascem preenchidos com o que está no cadastro (ver
      // seed do estado `itens`), não editar não muda nada aqui.
      const localizados = itens_resolvidos.filter(it => it.produto_id);
      // codigo_barras_embalagem: o código de barras da nota (em geral o da
      // caixa) entra no cadastro do produto com a Qtde Prod. — o próximo XML
      // reconhece por ele e, no PDV, bipar a caixa lança as unidades dela.
      const resultados = await Promise.allSettled(
        localizados.map(it =>
          api.patch(`/produtos/${it.produto_id}/fiscal`, {
            fiscal_entrada: it._fe,
            fiscal_saida:   it._fs,
            nome:               (it.descricao_produto || '').trim() || undefined,
            descricao_resumida: (it.descricao_produto || '').trim().slice(0, 40) || undefined,
            fator_conversao:    it.qtde_produto || 1,
            codigo_barras_embalagem: it._ean
              ? { codigo: it._ean, quantidade: it.qtde_produto || 1, unidade_xml: it.unidade, atualizar_quantidade: it._novo }
              : undefined,
          })
        )
      );
      const avisoCodigo = { outro_produto: [], quantidade_faltando: [] };
      resultados.forEach((r, i) => {
        const st = r.status === 'fulfilled' ? r.value?.data?.codigo_embalagem : null;
        if (avisoCodigo[st]) avisoCodigo[st].push(`${localizados[i].produto_nome} (${localizados[i]._ean})`);
      });
      if (avisoCodigo.outro_produto.length || avisoCodigo.quantidade_faltando.length) {
        alert([
          avisoCodigo.quantidade_faltando.length && `Código de barras da caixa NÃO gravado — a nota veio em caixa e a Qtde Prod. ficou 1 (o PDV venderia 1 unidade ao bipar a caixa). Informe a quantidade da caixa no cadastro do produto:\n- ${avisoCodigo.quantidade_faltando.join('\n- ')}`,
          avisoCodigo.outro_produto.length && `Código de barras da nota já pertence a OUTRO produto (não foi alterado):\n- ${avisoCodigo.outro_produto.join('\n- ')}`,
        ].filter(Boolean).join('\n\n'));
      }

      onConfirmar({
        fornecedor_id:     fornecedorId,
        tipo_movimento_id: tipoMovimentoId || null,
        dados,
        itens:             itens_resolvidos,
        transporte,
        xml_original:      xmlContent,
      });
    } finally {
      setConfirmando(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: '#f0f0f0', display: 'flex', flexDirection: 'column', fontFamily: 'Tahoma, sans-serif', fontSize: 13 }}>

      {/* ── HEADER ── */}
      <div className={s.titleBar} style={{ justifyContent: 'space-between' }}>
        <span>📄 Importação de NF-e {dados.numero_nf ? `— Nº ${dados.numero_nf}` : ''}</span>
        <button style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16 }}
          onClick={onCancelar}>✕</button>
      </div>

      {/* ── CORPO (scroll) ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>

        {/* SECTION 1 — Validação */}
        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Validação</legend>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className={`${s.badge} ${validacao.xml_valido ? s.badgeAprovado : s.badgeCancelado}`}>
              {validacao.xml_valido ? '✅ XML válido' : '❌ XML inválido'}
            </span>
            <span className={`${s.badge} ${s.badgeAberto}`}>{validacao.tipo_documento || 'Documento'}</span>
            {chaveDuplicada && (
              <span className={`${s.badge} ${s.badgeCancelado}`}>
                ⚠ NF já importada{validacao.entrada_existente ? ` (Entrada #${validacao.entrada_existente.codigo})` : ''}
              </span>
            )}
            {errosValidacao.map((e, i) => (
              <span key={i} className={`${s.badge} ${s.badgeCancelado}`}>⚠ {e}</span>
            ))}
            {dados.chave_acesso && (
              <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 11, color: '#555' }}>
                {dados.chave_acesso}
              </span>
            )}
          </div>
        </fieldset>

        {/* SECTION 2 — Fornecedor */}
        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Fornecedor</legend>
          {fornecedorOk ? (
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <span className={`${s.badge} ${s.badgeAprovado}`}>✓ Fornecedor localizado</span>
              <span><strong>Código:</strong> {fornecedorCad?.codigo ?? '—'}</span>
              <span><strong>Razão Social:</strong> {fornecedorCad?.razao_social || fornecedorCad?.nome_fantasia || fornecedor.dados_xml?.razao_social}</span>
              <span><strong>CNPJ/CPF:</strong> {fornecedorCad?.cpf_cnpj || fornecedor.dados_xml?.cnpj || fornecedor.dados_xml?.cpf}</span>
            </div>
          ) : (
            <div style={{ background: '#ffebee', border: '1px solid #ef9a9a', padding: 10, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ color: '#b71c1c', fontWeight: 'bold' }}>
                Fornecedor não cadastrado — {fornecedor.dados_xml?.razao_social} ({fornecedor.dados_xml?.cnpj || fornecedor.dados_xml?.cpf})
              </span>
              <span style={{ fontSize: 11.5, color: '#b71c1c' }}>
                Cadastro obrigatório para prosseguir com a importação.
              </span>
              <span style={{ marginLeft: 'auto' }}>
                <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={() => setShowCadFornec(true)}>Cadastrar</button>
              </span>
            </div>
          )}
        </fieldset>

        {/* SECTION 3 — Grid de Itens */}
        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Itens da NF ({itens.length})</legend>

          {/* Seletor de Tipo de Movimento — acima da grade para que conversões sejam visíveis */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: '6px 8px', background: '#eef4ff', border: '1px solid #c3d8f5', borderRadius: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#1a3a6b', whiteSpace: 'nowrap' }}>Tipo de Movimento:</span>
            <select
              className={s.selectErp}
              style={{ flex: 1, minWidth: 220, maxWidth: 400, height: 28, fontSize: 12 }}
              value={tipoMovimentoId}
              onChange={e => setTipoMovimentoId(e.target.value)}>
              <option value="">— selecione para aplicar conversões de CFOP/CST —</option>
              {tiposEntrada.map(t => (
                <option key={t.id} value={t.id}>{t.nome}</option>
              ))}
            </select>
            {tipoMovimentoId && Object.keys(cfopConvMap).length > 0 && (
              <span style={{ fontSize: 11, color: '#2e7d32', fontWeight: 600, whiteSpace: 'nowrap' }}>
                ✔ {Object.keys(cfopConvMap).length} conv. CFOP
                {Object.keys(cstIcmsConvMap).length > 0 && ` · ${Object.keys(cstIcmsConvMap).length} CST ICMS`}
                {Object.keys(cstPisConvMap).length > 0 && ` · ${Object.keys(cstPisConvMap).length} CST PIS/COF`}
              </span>
            )}
            {tipoMovimentoId && Object.keys(cfopConvMap).length === 0 && (
              <span style={{ fontSize: 11, color: '#b45309', whiteSpace: 'nowrap' }}>
                ⚠ Nenhuma conversão configurada para este tipo
              </span>
            )}
          </div>

          {/* Barra de cadastro em lote */}
          {temSelecionados && (
            <div style={{ background: '#e8f4fd', border: '1px solid #90caf9', borderRadius: 4, padding: '8px 10px', marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, color: '#1a56db', fontSize: 12, alignSelf: 'center' }}>
                  {selecionados.size} selecionado(s) — Cadastrar em lote:
                </span>

                {/* Unidade */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <label style={{ fontSize: 10, fontWeight: 600 }}>Unidade</label>
                  <div style={{ display: 'flex', gap: 3 }}>
                    <select className={s.selectErp} style={{ height: 28, padding: '2px 6px', fontSize: 12, width: 150 }}
                      value={cadBatch.unidade_id}
                      onChange={e => setCadBatch(b => ({ ...b, unidade_id: e.target.value }))}>
                      <option value="">—</option>
                      {unidades.map(u => <option key={u.id} value={u.id}>{u.codigo}{u.descricao ? ` — ${u.descricao}` : ''}</option>)}
                    </select>
                    <button className={s.btnErp} style={{ fontSize: 12, padding: '0 8px', height: 28, minWidth: 28 }}
                      title="Cadastrar nova unidade"
                      onClick={() => setNovaUnidade(v => ({ ...v, show: !v.show, codigo: '', descricao: '' }))}>+</button>
                  </div>
                </div>

                {/* Grupo */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <label style={{ fontSize: 10, fontWeight: 600 }}>Grupo</label>
                  <div style={{ display: 'flex', gap: 3 }}>
                    <select className={s.selectErp} style={{ height: 28, padding: '2px 6px', fontSize: 12, width: 220 }}
                      value={cadBatch.grupo_id}
                      onChange={e => setCadBatch(b => ({ ...b, grupo_id: e.target.value, subgrupo_id: '' }))}>
                      <option value="">—</option>
                      {grupos.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
                    </select>
                    <button className={s.btnErp} style={{ fontSize: 12, padding: '0 8px', height: 28, minWidth: 28 }}
                      title="Cadastrar novo grupo"
                      onClick={() => setNovoGrupo(v => ({ ...v, show: !v.show, nome: '' }))}>+</button>
                  </div>
                </div>

                {/* Subgrupo */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <label style={{ fontSize: 10, fontWeight: 600 }}>Subgrupo</label>
                  <div style={{ display: 'flex', gap: 3 }}>
                    <select className={s.selectErp} style={{ height: 28, padding: '2px 6px', fontSize: 12, width: 220 }}
                      value={cadBatch.subgrupo_id}
                      onChange={e => setCadBatch(b => ({ ...b, subgrupo_id: e.target.value }))}>
                      <option value="">—</option>
                      {subgrupos.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
                    </select>
                    <button className={s.btnErp} style={{ fontSize: 12, padding: '0 8px', height: 28, minWidth: 28 }}
                      title="Cadastrar novo subgrupo"
                      disabled={!cadBatch.grupo_id}
                      onClick={() => setNovoSubgrupo(v => ({ ...v, show: !v.show, nome: '' }))}>+</button>
                  </div>
                </div>

                <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={handleCadastrarLote} disabled={cadastrando}>
                  {cadastrando ? 'Cadastrando...' : 'CADASTRAR'}
                </button>
              </div>

              {/* Mini-form: Nova Unidade */}
              {novaUnidade.show && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', marginTop: 6, padding: '6px 8px', background: '#fff', border: '1px solid #bdd7f5', borderRadius: 3 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>Nova unidade:</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <label style={{ fontSize: 10 }}>Sigla *</label>
                    <input className={s.inputErp} style={{ width: 60, height: 28, padding: '2px 8px', fontSize: 12 }}
                      value={novaUnidade.codigo} placeholder="UN"
                      onChange={e => setNovaUnidade(v => ({ ...v, codigo: e.target.value.toUpperCase() }))} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <label style={{ fontSize: 10 }}>Descrição</label>
                    <input className={s.inputErp} style={{ width: 160, height: 28, padding: '2px 8px', fontSize: 12 }}
                      value={novaUnidade.descricao} placeholder="Unidade"
                      onChange={e => setNovaUnidade(v => ({ ...v, descricao: e.target.value }))} />
                  </div>
                  <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ fontSize: 11, padding: '1px 8px' }}
                    disabled={novaUnidade.salvando || !novaUnidade.codigo.trim()}
                    onClick={async () => {
                      setNovaUnidade(v => ({ ...v, salvando: true }));
                      try {
                        const { data } = await api.post('/unidades', { codigo: novaUnidade.codigo.trim(), descricao: novaUnidade.descricao.trim() });
                        const novasUnidades = [...unidades, data];
                        setUnidades(novasUnidades);
                        setCadBatch(b => ({ ...b, unidade_id: String(data.id) }));
                        setNovaUnidade({ show: false, codigo: '', descricao: '', salvando: false });
                      } catch (err) { alert(err.response?.data?.erro || 'Erro ao salvar unidade.'); setNovaUnidade(v => ({ ...v, salvando: false })); }
                    }}>Salvar</button>
                  <button className={s.btnErp} style={{ fontSize: 11 }} onClick={() => setNovaUnidade(v => ({ ...v, show: false }))}>✕</button>
                </div>
              )}

              {/* Mini-form: Novo Grupo */}
              {novoGrupo.show && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', marginTop: 6, padding: '6px 8px', background: '#fff', border: '1px solid #bdd7f5', borderRadius: 3 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>Novo grupo:</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <label style={{ fontSize: 10 }}>Nome *</label>
                    <input className={s.inputErp} style={{ width: 200, height: 28, padding: '2px 8px', fontSize: 12 }}
                      value={novoGrupo.nome} placeholder="Nome do grupo"
                      onChange={e => setNovoGrupo(v => ({ ...v, nome: e.target.value }))} />
                  </div>
                  <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ fontSize: 11, padding: '1px 8px' }}
                    disabled={novoGrupo.salvando || !novoGrupo.nome.trim()}
                    onClick={async () => {
                      setNovoGrupo(v => ({ ...v, salvando: true }));
                      try {
                        const { data } = await api.post('/grupos', { nome: novoGrupo.nome.trim() });
                        const novosGrupos = [...grupos, data];
                        setGrupos(novosGrupos);
                        setCadBatch(b => ({ ...b, grupo_id: String(data.id), subgrupo_id: '' }));
                        setNovoGrupo({ show: false, nome: '', salvando: false });
                      } catch (err) { alert(err.response?.data?.erro || 'Erro ao salvar grupo.'); setNovoGrupo(v => ({ ...v, salvando: false })); }
                    }}>Salvar</button>
                  <button className={s.btnErp} style={{ fontSize: 11 }} onClick={() => setNovoGrupo(v => ({ ...v, show: false }))}>✕</button>
                </div>
              )}

              {/* Mini-form: Novo Subgrupo */}
              {novoSubgrupo.show && cadBatch.grupo_id && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', marginTop: 6, padding: '6px 8px', background: '#fff', border: '1px solid #bdd7f5', borderRadius: 3 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>Novo subgrupo:</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <label style={{ fontSize: 10 }}>Nome *</label>
                    <input className={s.inputErp} style={{ width: 200, height: 28, padding: '2px 8px', fontSize: 12 }}
                      value={novoSubgrupo.nome} placeholder="Nome do subgrupo"
                      onChange={e => setNovoSubgrupo(v => ({ ...v, nome: e.target.value }))} />
                  </div>
                  <button className={`${s.btnErp} ${s.btnErpPrimario}`} style={{ fontSize: 11, padding: '1px 8px' }}
                    disabled={novoSubgrupo.salvando || !novoSubgrupo.nome.trim()}
                    onClick={async () => {
                      setNovoSubgrupo(v => ({ ...v, salvando: true }));
                      try {
                        const { data } = await api.post('/subgrupos', { nome: novoSubgrupo.nome.trim(), grupo_id: cadBatch.grupo_id });
                        const novosSub = [...subgrupos, data];
                        setSubgrupos(novosSub);
                        setCadBatch(b => ({ ...b, subgrupo_id: String(data.id) }));
                        setNovoSubgrupo({ show: false, nome: '', salvando: false });
                      } catch (err) { alert(err.response?.data?.erro || 'Erro ao salvar subgrupo.'); setNovoSubgrupo(v => ({ ...v, salvando: false })); }
                    }}>Salvar</button>
                  <button className={s.btnErp} style={{ fontSize: 11 }} onClick={() => setNovoSubgrupo(v => ({ ...v, show: false }))}>✕</button>
                </div>
              )}
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table className={s.itensGrid}>
              <thead>
                <tr>
                  <th style={{ width: 28 }}>
                    <input
                      type="checkbox"
                      checked={allNaoLocChecked}
                      onChange={toggleSelectAll}
                      title="Selecionar todos não localizados"
                    />
                  </th>
                  <th style={{ width: 80 }}>Status</th>
                  <th style={{ width: 28 }}>#</th>
                  <th style={{ width: 80 }}>Cód.Forn</th>
                  <th style={{ width: 110 }}>EAN</th>
                  <th style={{ minWidth: 200 }}>Descrição (XML)</th>
                  <th style={{ minWidth: 320 }}>Desc. Produto</th>
                  <th style={{ minWidth: 180 }}>Produto Vinculado</th>
                  <th style={{ width: 42 }}>UN</th>
                  <th style={{ width: 70, textAlign: 'right' }}>Qtde</th>
                  <th style={{ width: 55, textAlign: 'right' }}>Qtde Prod.</th>
                  <th style={{ width: 80, textAlign: 'right' }}>Vl.Unit</th>
                  <th style={{ width: 90, textAlign: 'right' }}>Vl.Total</th>
                  <th style={{ width: 80 }}>NCM</th>
                  <th style={{ width: 70 }}>CFOP</th>
                  <th style={{ width: 56 }}>CSOSN</th>
                  <th style={{ width: 42 }}></th>
                </tr>
              </thead>
              <tbody>
                {itens.map(it => {
                  const prodInativo = !!it.produto_inativo_id && it.produto_id === it.produto_inativo_id;
                  const st = prodInativo ? STATUS_ICON.inativo : (STATUS_ICON[it.status] || STATUS_ICON.nao_encontrado);
                  return (
                    <React.Fragment key={it._key}>
                      <tr style={prodInativo ? { background: '#ffedd5' } : undefined}
                        title={prodInativo ? 'Produto inativo no cadastro — volta a ser ativo ao confirmar a entrada' : undefined}>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selecionados.has(it._key)}
                            onChange={() => toggleSel(it._key)}
                            disabled={!!it.produto_id}
                          />
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }} title={st.txt}>
                          <span>{st.ico}</span> <span style={{ fontSize: 11, color: st.cor }}>{st.txt}</span>
                        </td>
                        <td className={s.tdSeq}>{it.seq}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{it.codigo_fornecedor}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{it.ean || '—'}</td>
                        <td title={it.descricao}>{it.descricao}</td>
                        <td>
                          <input
                            className={s.cellInput}
                            style={{ width: 310, fontSize: 11 }}
                            value={it.descricao_produto || ''}
                            title={it.produto_id ? 'Ajustar aqui atualiza a descrição do produto já cadastrado ao confirmar' : ''}
                            onChange={e => patchItem(it._key, { descricao_produto: e.target.value })}
                          />
                        </td>
                        <td>
                          {it.produto_id ? (
                            <span style={{ color: prodInativo ? '#c2410c' : '#2e7d32' }}>
                              [{it.produto_codigo}] {it.produto_nome}
                              {prodInativo && <span style={{ fontSize: 10, fontStyle: 'italic' }}> — inativo, será reativado</span>}
                            </span>
                          ) : (
                            <span style={{ color: '#b71c1c', fontStyle: 'italic' }}>Não localizado</span>
                          )}
                        </td>
                        <td>{it.unidade}</td>
                        <td style={{ textAlign: 'right' }}>{fmtNum(it.quantidade)}</td>
                        <td>
                          <input
                            className={s.cellInput}
                            style={{ width: 55, textAlign: 'right', fontSize: 11 }}
                            type="number"
                            min="1"
                            value={it.qtde_produto ?? 1}
                            title={it.produto_id ? 'Quantidade por embalagem — ajustar aqui atualiza o produto já cadastrado ao confirmar' : 'Quantidade por embalagem'}
                            onChange={e => patchItem(it._key, { qtde_produto: parseFloat(e.target.value) || 1 })}
                          />
                        </td>
                        <td style={{ textAlign: 'right' }}>{fmtNum(it.valor_unitario)}</td>
                        <td style={{ textAlign: 'right' }}>{fmtMoeda(it.valor_total)}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{it.ncm}</td>
                        <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                          {cfopConvMap[it.cfop] ? (
                            <span title={`XML: ${it.cfop} → Entrada: ${cfopConvMap[it.cfop]}`}>
                              <span style={{ color: 'var(--text-muted)', textDecoration: 'line-through' }}>{it.cfop}</span>
                              <span style={{ color: 'var(--success)', fontWeight: 700 }}> → {cfopConvMap[it.cfop]}</span>
                            </span>
                          ) : (
                            <span title={CFOP_CONV[it.cfop]?.desc || it.cfop}>{cfopLabel(it.cfop)}</span>
                          )}
                        </td>
                        <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                          {cstIcmsConvMap[it.csosn] ? (
                            <span title={`XML: ${it.csosn} → Ent: ${cstIcmsConvMap[it.csosn].entrada} / Saí: ${cstIcmsConvMap[it.csosn].saida}`}>
                              <span style={{ color: 'var(--text-muted)', textDecoration: 'line-through' }}>{it.csosn}</span>
                              <span style={{ color: '#38a169', fontWeight: 700 }}> {cstIcmsConvMap[it.csosn].entrada}</span>
                            </span>
                          ) : (
                            <span title={csosnLabel(it.csosn)}>{it.csosn}</span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                          <button className={`${s.btnErp} ${s.btnErpSmall}`} title="Localizar produto"
                            onClick={() => setBuscaProdKey(it._key)}>🔍</button>
                        </td>
                      </tr>
                      {expandKey === it._key && (
                        <tr>
                          <td colSpan={17} style={{ background: '#f5f8ff', padding: 8 }}>
                            <CadastroProdutoInline
                              item={it}
                              cstIcmsConvMap={cstIcmsConvMap}
                              cstPisConvMap={cstPisConvMap}
                              cfopConvMap={cfopConvMap}
                              onCancelar={() => setExpandKey(null)}
                              onSalvo={async (produto) => {
                                vincularIrmaos(it, {
                                  produto_id: produto.id, produto_nome: produto.nome,
                                  produto_codigo: produto.codigo_interno,
                                  status: 'encontrado', match_tipo: 'manual',
                                });
                                patchItem(it._key, {
                                  produto_id:     produto.id,
                                  produto_nome:   produto.nome,
                                  produto_codigo: produto.codigo_interno,
                                  status:         'novo',
                                  match_tipo:     'novo',
                                });
                                setExpandKey(null);
                                await salvarAssociacao(it, produto.id);
                              }}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </fieldset>

        {/* SECTION 4 — Resumo Financeiro */}
        <div>
          <SecaoHeader titulo="Resumo Financeiro" aberto={openFin} onToggle={() => setOpenFin(o => !o)}
            extra={`${(financeiro.duplicatas || []).length} duplicata(s) · ${(financeiro.formas_pagamento || []).length} forma(s)`} />
          {openFin && (
            <div className={s.fieldset} style={{ marginTop: 0 }}>
              <div className={s.dualGrid}>
                <div>
                  <strong style={{ color: '#003087' }}>Duplicatas</strong>
                  <table className={s.modalTabela} style={{ marginTop: 4 }}>
                    <thead><tr><th>Nº</th><th>Vencimento</th><th style={{ textAlign: 'right' }}>Valor</th></tr></thead>
                    <tbody>
                      {(financeiro.duplicatas || []).length === 0 && (
                        <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888' }}>Sem duplicatas no XML</td></tr>
                      )}
                      {(financeiro.duplicatas || []).map((d, i) => (
                        <tr key={i}><td>{d.numero}</td><td>{fmtData(d.vencimento)}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoeda(d.valor)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                  {(financeiro.duplicatas || []).length > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button className={`${s.btnErp} ${contasJaGeradas ? '' : s.btnErpPrimario}`}
                        onClick={handleGerarContasPagar}
                        disabled={gerandoContas || contasJaGeradas || !fornecedorOk}
                        title={contasJaGeradas ? 'Contas já foram geradas para esta NF'
                          : !fornecedorOk ? 'Cadastre o fornecedor antes de gerar as contas a pagar' : ''}>
                        {gerandoContas ? 'Gerando...' : contasJaGeradas ? '✓ Contas geradas' : '💰 Gerar Contas a Pagar'}
                      </button>
                      {contasJaGeradas && (
                        <span style={{ fontSize: 11, color: '#38a169', fontWeight: 600 }}>
                          {(financeiro.duplicatas || []).length} duplicata(s) lançada(s)
                        </span>
                      )}
                    </div>
                  )}
                  {(financeiro.duplicatas || []).length === 0 && (
                    <div style={{ marginTop: 8 }}>
                      <button className={s.btnErp} onClick={abrirCPAvulso} disabled={!fornecedorOk}
                        title={!fornecedorOk ? 'Cadastre o fornecedor antes de lançar o contas a pagar' : 'Lançar manualmente um título a pagar pra esta nota'}>
                        💰 Contas a Pagar avulso
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <strong style={{ color: '#003087' }}>Formas de Pagamento</strong>
                  <table className={s.modalTabela} style={{ marginTop: 4 }}>
                    <thead><tr><th>Tipo</th><th style={{ textAlign: 'right' }}>Valor</th></tr></thead>
                    <tbody>
                      {(financeiro.formas_pagamento || []).length === 0 && (
                        <tr><td colSpan={2} style={{ textAlign: 'center', color: '#888' }}>Não informado</td></tr>
                      )}
                      {(financeiro.formas_pagamento || []).map((p, i) => (
                        <tr key={i}><td>{TPAG_LABEL[p.tipo] || p.tipo}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoeda(p.valor)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* SECTION 5 — Transporte */}
        <div>
          <SecaoHeader titulo="Transporte" aberto={openTransp} onToggle={() => setOpenTransp(o => !o)}
            extra={transporte.transportadora || TIPO_FRETE_LABEL[transporte.tipo_frete] || ''} />
          {openTransp && (
            <div className={s.fieldset} style={{ marginTop: 0 }}>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <span><strong>Frete:</strong> {TIPO_FRETE_LABEL[transporte.tipo_frete] || transporte.tipo_frete}</span>
                <span><strong>Transportadora:</strong> {transporte.transportadora || '—'}</span>
                <span><strong>Placa:</strong> {transporte.placa || '—'} {transporte.uf_veiculo}</span>
                <span><strong>Peso Bruto:</strong> {fmtNum(transporte.peso_bruto)} kg</span>
                <span><strong>Peso Líq.:</strong> {fmtNum(transporte.peso_liquido)} kg</span>
                <span><strong>Volumes:</strong> {fmtNum(transporte.quantidade_vol)} {transporte.especie_vol}</span>
              </div>
            </div>
          )}
        </div>

        {/* SECTION 6 — Totais */}
        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Totais</legend>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {[
              ['Produtos', dados.valor_produtos],
              ['Frete', dados.valor_frete],
              ['Seguro', dados.valor_seguro],
              ['Desconto', dados.valor_desconto],
              ['IPI', dados.valor_ipi],
              ['ICMS ST', dados.valor_icms_st],
            ].map(([l, v]) => (
              <span key={l}><strong>{l}:</strong> {fmtMoeda(v)}</span>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: 15 }}>
              <strong>Total da NF: {fmtMoeda(dados.valor_total)}</strong>
            </span>
          </div>
        </fieldset>

      </div>

      {/* ── FOOTER ── */}
      <div className={s.footer} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, color: '#333' }}>
            <strong>{resumo.localizados}</strong> de <strong>{resumo.total}</strong> produtos localizados
            {resumo.naoLocal > 0 && <span style={{ color: '#b71c1c' }}> · {resumo.naoLocal} não localizado(s)</span>}
            {chaveDuplicada && (
              <span style={{ color: '#b71c1c', marginLeft: 10 }}>
                Esta NF já foi importada{validacao.entrada_existente ? ` (Entrada #${validacao.entrada_existente.codigo})` : ''}.
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className={`${s.btnErp} ${s.btnErpPrimario}`}
              onClick={handleConfirmar} disabled={!podeConfirmar || confirmando}
              title={!fornecedorOk ? 'Cadastre o fornecedor antes de confirmar a importação'
                : !podeConfirmar ? 'Importação bloqueada por validação' : ''}>
              {confirmando ? 'Atualizando fiscal...' : 'Confirmar Importação'}
            </button>
            <button className={s.btnErp} onClick={onCancelar}>Cancelar</button>
          </div>
        </div>
        {errosValidacao.length > 0 && (
          <div className={s.statusErro}>Pendências: {errosValidacao.join(' · ')}</div>
        )}
      </div>

      {/* ── Modais sobrepostos ── */}
      {buscaProdKey !== null && (
        <ModalBuscaProduto
          onClose={() => setBuscaProdKey(null)}
          onSelect={(p) => onSelecionarProduto(buscaProdKey, p)}
        />
      )}
      {showCadFornec && (
        <ModalCadastroFornecedorRapido
          dadosXml={fornecedor.dados_xml || {}}
          onCancelar={() => setShowCadFornec(false)}
          onSalvo={(forn) => {
            setFornecedorId(forn.id);
            setFornecedorCad(forn);
            setShowCadFornec(false);
          }}
        />
      )}
      {modalCPAvulso && (
        <ModalNovaContaPagar
          fornecedores={fornecedorCad ? [fornecedorCad] : []}
          contasFinanc={contasFinancCP}
          planoContas={planoContasCP}
          onSalvo={() => { setModalCPAvulso(false); alert('Contas a pagar lançada.'); }}
          onClose={() => setModalCPAvulso(false)}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════
   Cadastro inline de Produto (linha expandida)
══════════════════════════════════ */
function CadastroProdutoInline({ item, onSalvo, onCancelar, cfopConvMap = {}, cstIcmsConvMap = {}, cstPisConvMap = {} }) {
  const margemInicial = 30;
  const custo = Number(item.valor_unitario || 0);
  const cfopConv = cfopConvMap[item.cfop] || item.cfop || '';
  const [p, setP] = useState({
    nome:               item.descricao || '',
    descricao_resumida: (item.descricao || '').slice(0, 40),
    ncm:                item.ncm || '',
    cfop:               cfopConv,
    unidade:            item.unidade || 'UN',
    codigo_barras:      item.ean || item.ean_cprod || '',
    codigo_fornecedor:  item.codigo_fornecedor || '',
    preco_custo:        custo ? custo.toFixed(2) : '0',
    margem_pct:         String(margemInicial),
    preco_venda:        custo ? (custo * (1 + margemInicial / 100)).toFixed(2) : '0',
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro]         = useState('');

  const set = (k, v) => setP(prev => ({ ...prev, [k]: v }));

  // Recalcula preço de venda ao mudar custo/margem
  const setCusto = v => {
    const c = parseFloat(v) || 0; const m = parseFloat(p.margem_pct) || 0;
    setP(prev => ({ ...prev, preco_custo: v, preco_venda: (c * (1 + m / 100)).toFixed(2) }));
  };
  const setMargem = v => {
    const c = parseFloat(p.preco_custo) || 0; const m = parseFloat(v) || 0;
    setP(prev => ({ ...prev, margem_pct: v, preco_venda: (c * (1 + m / 100)).toFixed(2) }));
  };

  const salvar = async () => {
    if (!p.nome.trim()) { setErro('Descrição é obrigatória.'); return; }
    setSalvando(true); setErro('');
    try {
      const cstXml   = item.csosn || item.cst_icms || '';
      const pisXml   = item.cst_pis || '';
      const icmsConv = cstIcmsConvMap[cstXml];
      const pisConv  = cstPisConvMap[pisXml];
      const payload = {
        nome:               p.nome.trim(),
        descricao_resumida: p.descricao_resumida.trim(),
        unidade:            p.unidade.trim() || 'UN',
        ncm:                p.ncm.trim() || null,
        cfop_saida:         CFOP_CONV[p.cfop.trim()]?.saida || p.cfop.trim() || null,
        ean_fiscal:         p.codigo_barras.trim() || null,
        preco_custo:        parseFloat(p.preco_custo) || 0,
        margem_pct:         parseFloat(p.margem_pct) || 0,
        preco_venda:        parseFloat(p.preco_venda) || 0,
        ativo:              true,
        inativo:            false,
        codigos_barras:     p.codigo_barras.trim()
          ? [{ codigo: p.codigo_barras.trim(), tipo: 'EAN-13', principal: true }]
          : [],
        fiscal_entrada: {
          ...FISCAL_ICMS_DEFAULT,
          icms_cst: icmsConv?.entrada || '',
          pis_habilitado: !!pisConv?.entrada, pis_cst: pisConv?.entrada || '',
          cofins_habilitado: !!pisConv?.entrada, cofins_cst: pisConv?.entrada || '',
        },
        fiscal_saida: {
          ...FISCAL_ICMS_DEFAULT,
          icms_cst: icmsConv?.saida || '',
          pis_habilitado: !!pisConv?.saida, pis_cst: pisConv?.saida || '',
          cofins_habilitado: !!pisConv?.saida, cofins_cst: pisConv?.saida || '',
        },
      };
      const { data } = await api.post('/produtos', payload);
      onSalvo(data);
    } catch (err) {
      const msg = err.response?.data?.erros?.[0]?.msg || err.response?.data?.erro || 'Erro ao cadastrar produto.';
      setErro(msg);
      setSalvando(false);
    }
  };

  const campo = (label, k, w, onCh) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, width: w }}>
      <label style={{ fontSize: 10, color: '#555', fontWeight: 'bold' }}>{label}</label>
      <input className={s.inputErp} style={{ height: 28, padding: '2px 8px' }} value={p[k]}
        onChange={e => (onCh ? onCh(e.target.value) : set(k, e.target.value))} />
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <strong style={{ color: '#1a56db', fontSize: 12 }}>Cadastrar novo produto a partir do XML</strong>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {campo('Descrição', 'nome', 280)}
        {campo('Descrição Reduzida', 'descricao_resumida', 180)}
        {campo('NCM', 'ncm', 90)}
        {campo('CFOP', 'cfop', 60)}
        {campo('Unidade', 'unidade', 60)}
        {campo('Cód. Barras (EAN)', 'codigo_barras', 130)}
        {campo('Cód. Fornecedor', 'codigo_fornecedor', 110)}
        {campo('Preço Compra', 'preco_custo', 90, setCusto)}
        {campo('Margem %', 'margem_pct', 70, setMargem)}
        {campo('Preço Venda', 'preco_venda', 90)}
      </div>
      {erro && <div className={s.statusErro}>{erro}</div>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button className={`${s.btnErp} ${s.btnErpPrimario} ${s.btnErpSmall}`} onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : 'Salvar Produto'}
        </button>
        <button className={`${s.btnErp} ${s.btnErpSmall}`} onClick={onCancelar} disabled={salvando}>Cancelar</button>
      </div>
    </div>
  );
}
